/**
 * Server functions for managing an organization's proxy credentials.
 *
 * Each call re-checks the session and scopes every read/write to the caller's
 * **active organization** — a route `beforeLoad` guard does NOT protect a server
 * function. A credential is owned by an org, so we always resolve it by id AND
 * organizationId; one org can never touch another's credentials.
 *
 * These keep the vh_meteredproxy control plane (metered-proxy.ts) and our
 * Drizzle records in sync: create/delete/rotate/enable hit the backend first,
 * then our DB. Plaintext passwords are returned by the backend ONCE (on create
 * and rotate) and are never stored.
 *
 * Not marked `server-only`: this module exports `createServerFn`s that the
 * client imports as RPC stubs. The compiler strips the `.handler()` bodies (and
 * the server-only imports used only inside them — db, auth, metered-proxy) from
 * the client bundle.
 */
import { randomBytes } from 'node:crypto'
import { createServerFn } from '@tanstack/react-start'
import { and, asc, eq } from 'drizzle-orm'
import { z } from 'zod'

import { db } from '@/db'
import { proxyCredentials, usageSnapshots } from '@/db/schema'
import { totalRemainingBytes } from '@/lib/billing/billing'
import { actuateGroupCeiling } from '@/lib/billing/group-ceiling.server'
import {
  CredentialLimitError,
  credentialLimitForOrg,
  deleteCredentialOnRow,
  orgEntitled,
  readCycleRow,
  setCredentialEnabledOnRow,
} from '@/lib/credentials/actions.server'
import type { ProxyConnectionInfo } from '@/lib/credentials/credentials'
import { PublicError } from '@/lib/errors'
import { requireOrgMember } from '@/lib/org/require-org.server'
import { liveStatusByUsername } from '@/lib/proxy-status.server'
import { env } from '@/lib/server/env'
import * as proxy from '@/lib/server/metered-proxy'
import { emitBusinessEvent } from '@/lib/telemetry/business.server'
import { capBytesFromGb, planCredentialLimit } from '@/lib/usage/limits'
import { utcDayKey } from '@/lib/usage/usage'
import { optionalLabel, parseInput, id as zId } from '@/lib/validation'

// ---------------------------------------------------------------------------
// Guards / helpers
// ---------------------------------------------------------------------------

/**
 * Load a credential, asserting it belongs to the caller's active org.
 *
 * `destructive: true` additionally requires an owner/admin role. The org model
 * is Oxylabs-style — every member USES and manages credentials day-to-day
 * (create, enable/disable, caps, rotate-ip) — but the two UNRECOVERABLE ops are
 * held back: delete (destroys the backend user) and rotate-password (the
 * plaintext is shown once, so rotation breaks whatever the team deployed the
 * old string into). Documented in AGENTS.md; keep the two lists in sync.
 */
async function requireOwnedCredential(
  id: string,
  opts?: { destructive?: boolean },
) {
  const { organizationId, role } = await requireOrgMember()
  if (opts?.destructive && role === 'member') {
    throw new PublicError(
      'Only organization owners and admins can do this. Ask an admin, or have them promote your role.',
      403,
    )
  }
  const [row] = await db
    .select()
    .from(proxyCredentials)
    .where(
      and(
        eq(proxyCredentials.id, id),
        eq(proxyCredentials.organizationId, organizationId),
      ),
    )
    .limit(1)
  if (!row) throw new PublicError('Credential not found', 404)
  return row
}

/** Opaque, effectively-unique proxy username: `<prefix>_<10 hex>`. The prefix
 *  is env-driven (`PROXY_USERNAME_PREFIX`, default 'mlabs'; staging e.g.
 *  'staging') so a deployment sharing the prod control plane generates into
 *  its own namespace — see the env.ts comment. */
function generateUsername(): string {
  return `${env.PROXY_USERNAME_PREFIX}_${randomBytes(5).toString('hex')}`
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface CredentialView {
  id: string
  username: string
  label: string | null
  status: 'active' | 'suspended' | 'disabled'
  createdAt: string
  /** Live cumulative usage from the control plane; null if it's unreachable. */
  usageBytes: number | null
  /** Live enabled flag from the control plane; null if it's unreachable. */
  enabled: boolean | null
  /** Per-UTC-day traffic cap in bytes, or null for no daily cap. */
  dailyLimitBytes: number | null
  /** Per-UTC-month traffic cap in bytes, or null for no monthly cap. */
  monthlyLimitBytes: number | null
  /** Total traffic cap for the credential's whole life, or null for none. */
  lifetimeLimitBytes: number | null
  /** The lifetime cap's fixed anchor — spend is `usageBytes - this`. */
  lifetimeBaselineBytes: number | null
}

export const listCredentials = createServerFn({ method: 'GET' }).handler(
  async (): Promise<CredentialView[]> => {
    const { organizationId } = await requireOrgMember()
    const rows = await db
      .select()
      .from(proxyCredentials)
      .where(eq(proxyCredentials.organizationId, organizationId))
      .orderBy(asc(proxyCredentials.createdAt))

    // Best-effort live status per credential — tolerate the control plane being
    // down (or an individual credential having drifted) so the list still loads.
    // Shared with the admin org-detail view so both show the same live numbers.
    const live = await liveStatusByUsername(rows.map((r) => r.username))

    return rows.map((r) => {
      const status = live.get(r.username)
      return {
        id: r.id,
        username: r.username,
        label: r.label,
        status: r.status,
        createdAt: r.createdAt.toISOString(),
        usageBytes: status?.usageBytes ?? null,
        enabled: status?.enabled ?? null,
        dailyLimitBytes: r.dailyLimitBytes,
        monthlyLimitBytes: r.monthlyLimitBytes,
        lifetimeLimitBytes: r.lifetimeLimitBytes,
        lifetimeBaselineBytes: r.lifetimeBaselineBytes,
      }
    })
  },
)

export const getProxyConnectionInfo = createServerFn({ method: 'GET' }).handler(
  async (): Promise<ProxyConnectionInfo> => {
    // Authenticate + scope like every other read here: a beforeLoad guard does
    // NOT protect a server fn, and this endpoint is only ever shown to a signed-in
    // member building their proxy string. The values themselves aren't secret
    // (they're the public proxy host/ports), but an unauthenticated RPC fn still
    // violates the authorize-inside-every-fn rule and leaks infra endpoints to
    // anonymous callers — gate it behind a live membership.
    await requireOrgMember()
    // The public proxy endpoint customers connect to — distinct from the
    // control-plane API (METEREDPROXY_API_URL) and not a secret.
    return {
      host: env.PROXY_ENDPOINT_HOST,
      httpPort: env.PROXY_HTTP_PORT,
      httpsPort: env.PROXY_HTTPS_PORT,
      socksPort: env.PROXY_SOCKS_PORT,
      http3Port: env.PROXY_HTTP3_PORT,
    }
  },
)

/**
 * Distinct upstream location codes that can actually serve traffic right now,
 * for the endpoint builder's location picker. Derived from the live pool:
 * a location is offered only if it has at least one enabled, non-down proxy —
 * the backend fails a location request closed when none are available, so
 * offering a dead location would hand the customer an unexplained error.
 */
export const listAvailableLocations = createServerFn({ method: 'GET' }).handler(
  async (): Promise<string[]> => {
    await requireOrgMember()
    const pool = await proxy.getPool()
    const codes = new Set<string>()
    for (const p of pool) {
      if (p.enabled && !p.down && p.location) codes.add(p.location)
    }
    return [...codes].sort()
  },
)

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const createCredential = createServerFn({ method: 'POST' })
  .validator((d: { label?: string } | undefined) =>
    parseInput(z.object({ label: optionalLabel }), d ?? {}),
  )
  .handler(
    async ({
      data,
    }): Promise<{
      id: string
      username: string
      password: string
      /**
       * Whether the credential is live and metered right now. `true`: the group
       * was funded and the backend user was flipped on. `false`: created but
       * suspended — either the org has no funds, or the group-fund / enable call
       * failed. The metering cron re-funds the group and re-enables DB-active
       * (funded) credentials on its next tick; a truly unfunded one stays off
       * until the wallet is topped up. Callers should surface the suspended
       * state (e.g. "add data to activate") rather than treating it as ready.
       */
      active: boolean
    }> => {
      // Managing credentials is intentionally open to any org member (not just
      // owner/admin) — a product decision; `requireOrgMember`/`requireOwnedCredential`
      // still scope every op to the caller's active org, so one org can never
      // touch another's. What we DO cap is volume: each create provisions a
      // backend user and draws on the shared pool, so refuse past a per-org limit.
      const { organizationId } = await requireOrgMember()

      // Access gate: creating a credential requires an active subscription.
      // Without one the org can't provision or proxy at all (metering also
      // suspends existing credentials when the subscription lapses).
      if (!(await orgEntitled(organizationId))) {
        throw new PublicError(
          'Choose a plan to start using the proxy network. Subscribe from the billing page, then create credentials.',
        )
      }

      const [existingCount, limit] = await Promise.all([
        db.$count(
          proxyCredentials,
          eq(proxyCredentials.organizationId, organizationId),
        ),
        credentialLimitForOrg(organizationId),
      ])
      if (existingCount >= limit) {
        throw new CredentialLimitError(limit)
      }

      // Enforcement lives on the shared GROUP pool (group_id = org id), not on
      // the per-credential limit — that is caps-only and defaults to 0
      // (= unlimited). A new credential in a group the metering cron hasn't
      // reached yet would pass BOTH gates as unlimited (per-cred = 0, lazy group
      // = 0), a fully unmetered window. So we FUND THE GROUP before enabling:
      // create disabled, PUT the group's absolute ceiling from the DB cycle
      // fields, then enable only if the org has allowance left. If the group PUT
      // fails we leave the credential disabled rather than open that window.
      const cycle = await readCycleRow(organizationId)
      const remaining = totalRemainingBytes(
        cycle.cycleAllowanceBytes,
        cycle.creditBytes,
        cycle.groupUsedBytes,
        cycle.groupBaselineBytes,
      )
      const hasFunds = remaining > 0

      // Provision on the backend first (it generates the password), created
      // DISABLED so no traffic flows until the group is funded below. Pass the
      // group id so its usage draws down the shared pool. Retry on the rare
      // username clash before persisting our record.
      let created: proxy.CreatedUser | undefined
      let username = ''
      for (let attempt = 0; attempt < 5 && !created; attempt++) {
        username = generateUsername()
        try {
          created = await proxy.createUser({
            username,
            enabled: false,
            bandwidth_limit: 0,
            group_id: organizationId,
          })
        } catch (e) {
          if (
            e instanceof proxy.MeteredProxyError &&
            e.code === 'user_exists'
          ) {
            continue
          }
          throw e
        }
      }
      if (!created) {
        throw new Error('Could not allocate a unique proxy username')
      }
      if (created.password === null) {
        // Normal creation always returns a plaintext password; a null means the
        // backend was called in DR import mode, which this path never does.
        throw new Error('proxy did not return a password on create')
      }

      // Persist our row IMMEDIATELY after backend provisioning, before any
      // funding/enable work. This is what makes the maintenance orphan sweep
      // safe to HARD-DELETE backend users with no DB row: the unprotected
      // window (backend user exists, row doesn't) is these few milliseconds
      // against a daily sweep. A failed insert tears the backend user back
      // down (best-effort) so nothing is orphaned in either direction.
      let row: { id: string }
      try {
        ;[row] = await db
          .insert(proxyCredentials)
          .values({
            organizationId,
            username,
            label: data.label,
            status: 'suspended',
            // Mirror the backend's hash+salt so the maintenance reconciler can
            // re-provision this credential with its original password after a
            // Redis data-loss (never plaintext; see schema + reconciler).
            passwordHash: created.password_hash,
            passwordSalt: created.salt,
          })
          .returning({ id: proxyCredentials.id })
      } catch (e) {
        try {
          await proxy.deleteUser(username)
        } catch {
          // The daily orphan sweep hard-deletes it if this cleanup also fails.
        }
        throw e
      }

      // Anchor the usage-chart series at 0 for this credential (and once for its
      // org group) so the FIRST day of traffic shows real usage. The charts read
      // each day's bytes as the delta between consecutive cumulative snapshots
      // (usage/server.ts), bucketed by captured_at; with no predecessor the
      // first day reads 0, and a just-created backend user starts at 0, so a 0
      // row is truthful. Date it the day BEFORE creation, not the epoch: because
      // the chart buckets by captured_at, an epoch anchor dragged the all-time
      // axis back to 1970. The day-before row sorts before the first real
      // snapshot (giving it a zero to diff against) without that artifact.
      const anchorAt = new Date(Date.now() - 24 * 60 * 60 * 1000)
      const anchorDay = utcDayKey(anchorAt)
      await db
        .insert(usageSnapshots)
        .values({
          organizationId,
          credentialId: row.id,
          day: anchorDay,
          cumulativeBytes: 0,
          capturedAt: anchorAt,
        })
        .onConflictDoNothing()
      // The org's GROUP anchor is not written here: metering snapshots every
      // billing row every tick, so by the time an org creates a credential its
      // first group row usually already exists and an anchor written here would
      // be skipped as redundant (which is how orgs ended up with charts that
      // read zero). Metering writes that anchor with the org's first snapshot
      // instead — see usage/metering.server.ts.
      // Fund the group BEFORE enabling: push the pool's absolute ceiling via the
      // shared writer (the SINGLE clamp; same absolute the metering cron and the
      // money writers converge to, credit term and entitlement gate included).
      // No `getGroup` needed: the ceiling is a function of the committed cycle,
      // not the live counter. Only after this lands do we flip the credential on,
      // so it can never serve unmetered.
      let enabled = false
      if (hasFunds) {
        try {
          if (!(await actuateGroupCeiling(organizationId))) {
            throw new Error('group ceiling PUT failed')
          }
          await proxy.updateUser(username, { enabled: true })
          enabled = true
        } catch {
          // Group funding or enable failed — leave the credential disabled (and
          // the DB row suspended) rather than open an unmetered window. The
          // metering cron re-funds the group and re-enables DB-active credentials
          // on its next tick, but this one stays suspended until then.
        }
      }

      if (enabled) {
        await db
          .update(proxyCredentials)
          .set({ status: 'active' })
          .where(eq(proxyCredentials.id, row.id))
      }

      emitBusinessEvent('credential.create', {
        org_id: organizationId,
        credential_id: row.id,
        actor: 'user',
        active: enabled,
      })
      return {
        id: row.id,
        username,
        password: created.password,
        active: enabled,
      }
    },
  )

export const rotateCredentialPassword = createServerFn({ method: 'POST' })
  .validator((d: { id: string }) => parseInput(z.object({ id: zId }), d))
  .handler(async ({ data }): Promise<{ password: string }> => {
    // Destructive: the old password stops working and the new plaintext is
    // shown once — rotation breaks every deployment holding the old string.
    const row = await requireOwnedCredential(data.id, { destructive: true })
    const { password, password_hash, salt } = await proxy.rotatePassword(
      row.username,
    )
    // Refresh the mirrored hash+salt so DR recovery re-provisions the NEW
    // password, not the old one (also backfills rows predating these columns).
    await db
      .update(proxyCredentials)
      .set({ passwordHash: password_hash, passwordSalt: salt })
      .where(eq(proxyCredentials.id, data.id))
    emitBusinessEvent('credential.update', {
      org_id: row.organizationId,
      credential_id: row.id,
      actor: 'user',
      action: 'rotate_password',
    })
    return { password }
  })

export const rotateCredentialIp = createServerFn({ method: 'POST' })
  .validator((d: { id: string }) => parseInput(z.object({ id: zId }), d))
  .handler(async ({ data }): Promise<{ rotated: boolean }> => {
    const row = await requireOwnedCredential(data.id)
    const result = await proxy.rotateIp(row.username)
    emitBusinessEvent('credential.update', {
      org_id: row.organizationId,
      credential_id: row.id,
      actor: 'user',
      action: 'rotate_ip',
    })
    return result
  })

export const setCredentialEnabled = createServerFn({ method: 'POST' })
  .validator((d: { id: string; enabled: boolean }) =>
    parseInput(z.object({ id: zId, enabled: z.boolean() }), d),
  )
  .handler(async ({ data }): Promise<{ status: 'active' | 'disabled' }> => {
    const row = await requireOwnedCredential(data.id)
    const result = await setCredentialEnabledOnRow(row, data.enabled)
    emitBusinessEvent('credential.update', {
      org_id: row.organizationId,
      credential_id: row.id,
      actor: 'user',
      action: data.enabled ? 'enable' : 'disable',
    })
    return result
  })

/**
 * Rename a credential: updates the human-facing `label` only. The opaque proxy
 * `username` never changes, so a rename touches nothing on the control plane.
 * Label is normalized exactly like `createCredential`: trimmed, and an empty
 * string collapses to `null` (no label, falls back to the username in the UI).
 */
export const renameCredential = createServerFn({ method: 'POST' })
  .validator((d: { id: string; label?: string }) =>
    parseInput(z.object({ id: zId, label: optionalLabel }), d),
  )
  .handler(async ({ data }): Promise<{ label: string | null }> => {
    const row = await requireOwnedCredential(data.id)
    await db
      .update(proxyCredentials)
      .set({ label: data.label })
      .where(eq(proxyCredentials.id, row.id))
    emitBusinessEvent('credential.update', {
      org_id: row.organizationId,
      credential_id: row.id,
      actor: 'user',
      action: 'rename',
    })
    return { label: data.label }
  })

/**
 * Set (or clear) a credential's daily / monthly / lifetime traffic caps. Caps are
 * entered in GB; `null` clears a window. Persists the caps, rolls the rolling
 * baselines (and anchors the lifetime cap on first set), and actuates the backend
 * `bandwidth_limit` immediately (best-effort — if the control plane is
 * unreachable we still persist, and the metering pass reconciles the limit on its
 * next tick). See `lib/usage/limits.ts` for the windowing algorithm.
 *
 * Clearing the lifetime cap unanchors it, so setting it again grants a FULL fresh
 * allowance rather than resuming the old one — callers must say so in their copy.
 */
export const setCredentialLimits = createServerFn({ method: 'POST' })
  .validator(
    (d: {
      id: string
      dailyGb: number | null
      monthlyGb: number | null
      lifetimeGb: number | null
    }) => {
      // `capBytesFromGb` throws a plain Error on a bad value; convert it to a
      // `PublicError` so the user sees the reason instead of the generic sanitized
      // message (validator throws are also run through `sanitizeErrors`).
      const toBytes = (gb: number | null, label: string): number | null => {
        try {
          return capBytesFromGb(gb)
        } catch {
          throw new PublicError(
            `${label} limit must be a positive number of GB, or empty for no limit.`,
          )
        }
      }
      const { id } = parseInput(z.object({ id: zId }), d)
      return {
        id,
        dailyLimitBytes: toBytes(d.dailyGb, 'Daily'),
        monthlyLimitBytes: toBytes(d.monthlyGb, 'Monthly'),
        lifetimeLimitBytes: toBytes(d.lifetimeGb, 'Lifetime'),
      }
    },
  )
  .handler(
    async ({
      data,
    }): Promise<{
      dailyLimitBytes: number | null
      monthlyLimitBytes: number | null
      lifetimeLimitBytes: number | null
    }> => {
      const row = await requireOwnedCredential(data.id)

      // Read live usage + current backend limit; tolerate the control plane
      // being down (fall back to our billed high-water mark for the baseline).
      let currentUsed = row.lastUsageBytes
      let liveLimit = 0
      let reachable = true
      try {
        const status = await proxy.getUser(row.username)
        currentUsed = status.bandwidth_used
        liveLimit = status.bandwidth_limit
      } catch {
        reachable = false
      }

      const limits = {
        dailyLimitBytes: data.dailyLimitBytes,
        monthlyLimitBytes: data.monthlyLimitBytes,
        lifetimeLimitBytes: data.lifetimeLimitBytes,
      }
      const plan = planCredentialLimit({
        currentUsed,
        liveLimit,
        limits,
        daily: {
          baselineBytes: row.dailyBaselineBytes,
          periodKey: row.dailyBaselinePeriod,
        },
        monthly: {
          baselineBytes: row.monthlyBaselineBytes,
          periodKey: row.monthlyBaselinePeriod,
        },
        lifetime: row.lifetimeBaselineBytes,
        now: new Date(),
      })

      await db
        .update(proxyCredentials)
        .set({
          dailyLimitBytes: data.dailyLimitBytes,
          monthlyLimitBytes: data.monthlyLimitBytes,
          dailyBaselineBytes: plan.daily.baselineBytes,
          dailyBaselinePeriod: plan.daily.periodKey,
          monthlyBaselineBytes: plan.monthly.baselineBytes,
          monthlyBaselinePeriod: plan.monthly.periodKey,
          lifetimeLimitBytes: data.lifetimeLimitBytes,
          lifetimeBaselineBytes: plan.lifetime,
        })
        .where(eq(proxyCredentials.id, row.id))

      // Actuate now so the cap takes effect without waiting for a metering tick.
      // Skip when the credential is suspended (enabled:false owns the cutoff) or
      // the backend was unreachable — metering reconciles it later either way.
      if (reachable && row.status === 'active' && plan.shouldPatch) {
        try {
          await proxy.updateUser(row.username, {
            bandwidth_limit: plan.desiredLimit,
          })
        } catch {
          // Non-fatal: the caps are persisted; metering will re-actuate.
        }
      }

      emitBusinessEvent('credential.update', {
        org_id: row.organizationId,
        credential_id: row.id,
        actor: 'user',
        action: 'set_limits',
      })
      return {
        dailyLimitBytes: data.dailyLimitBytes,
        monthlyLimitBytes: data.monthlyLimitBytes,
        lifetimeLimitBytes: data.lifetimeLimitBytes,
      }
    },
  )

export const deleteCredential = createServerFn({ method: 'POST' })
  .validator((d: { id: string }) => parseInput(z.object({ id: zId }), d))
  .handler(async ({ data }): Promise<{ ok: true }> => {
    // Destructive: destroys the backend user and its counter, unrecoverably.
    const row = await requireOwnedCredential(data.id, { destructive: true })
    const result = await deleteCredentialOnRow(row)
    emitBusinessEvent('credential.delete', {
      org_id: row.organizationId,
      credential_id: row.id,
      actor: 'user',
    })
    return result
  })
